/**
 * The ReDoS screen for an operator-authored regex (a stored `regex` condition in
 * `bat.exchange_api_templates.pattern_rules`).
 *
 * WHY A PARSER AND NOT A REGEX OVER THE SOURCE. The previous screen was three
 * regexes run over the pattern text, and they matched far more than the
 * constructs they were named after: `/([^)]*|[^)]*)[+*]/` has a top-level `|`,
 * so its right half is just `[^)]*[+*]` and it refused ANY pattern holding a `+`
 * or `*` (`^\d+$`, `^BTC.*`, even the class `[+*]`); `/(.+)1+/` lacked its
 * backslash and refused any character followed by a literal `1`. Every regex
 * with a quantifier was dead in real imports, and the editor only said "no
 * match".
 *
 * This module tokenises the pattern (escapes, character classes, groups,
 * alternation, quantifiers) into a small tree and refuses only the shapes that
 * give a backtracking engine exponentially many ways to split the same input:
 *
 *   - `nested_quantifier`: a REPEATED group (`*`, `+`, `{n,}`, `{n,m}` with
 *     m > 1) whose body holds a variable quantifier (`(a+)+`, `(a*)*`, `(.*)*`,
 *     `(\w+\s?)*`) — unless a fixed delimiter the quantified parts cannot match
 *     opens or closes each iteration (`(\d{1,3},)*`, `(,\d+)*`), which makes the
 *     split of the input into iterations unique;
 *   - `overlapping_alternation`: a REPEATED group whose alternatives can start
 *     with the same character (`(a|a)+`, `(a|aa)*`, `(a|)+`);
 *   - `quantified_backreference`: `\1+`, `\k<name>*`, …
 *
 * It is CONSERVATIVE where it cannot prove safety (a nested group or a character
 * set it cannot compare counts as "may overlap"), and it never refuses a pattern
 * without a repeated group or a repeated backreference — plain quantifiers on
 * atoms (`^\d+$`, `^BTC.*`, `^-?0+(\.0+)?$`) are linear-time in practice.
 *
 * No runtime deps: the browser editor calls this too.
 */

// Longest pattern source any engine will compile — the same cap the address
// validators use, so one number governs every operator-authored regex.
import { REGEX_PATTERN_MAX_LENGTH } from '../../utils/regex-anchoring.js'

export type RegexPatternRejectionReason =
  | 'too_long'
  | 'invalid'
  | 'nested_quantifier'
  | 'overlapping_alternation'
  | 'quantified_backreference'

export type RegexPatternScreenResult =
  | { accepted: true }
  | {
      accepted: false
      reason: RegexPatternRejectionReason
      /** English, for logs and the server preview; the editor localizes `reason`. */
      message: string
    }

interface Quantifier {
  min: number
  max: number
}

type RegexNode =
  /** One literal character. `char` is lowercased: comparisons are conservative about case. */
  | { kind: 'char'; char: string; quantifier?: Quantifier }
  /** A set of characters: `.`, `[...]`, `\d`, `\p{…}`… `source` compiles on its own. */
  | { kind: 'set'; source: string; quantifier?: Quantifier }
  /** Zero-width: `^`, `$`, `\b`, `\B`. */
  | { kind: 'assertion'; quantifier?: Quantifier }
  | { kind: 'backreference'; quantifier?: Quantifier }
  | { kind: 'group'; branches: RegexNode[][]; quantifier?: Quantifier }

const CONTROL_ESCAPE_CHARS: Record<string, string> = {
  t: '\t',
  n: '\n',
  r: '\r',
  f: '\f',
  v: '\v',
  '0': '\0',
}

/**
 * Recursive-descent tokenizer. Runs only on a source `new RegExp` has already
 * accepted, so it can be lenient: anything it does not recognise is a literal.
 */
class RegexTreeParser {
  private index = 0

  constructor(private readonly source: string) {}

  parse(): RegexNode[][] {
    return this.parseBranches(false)
  }

  private parseBranches(insideGroup: boolean): RegexNode[][] {
    const branches: RegexNode[][] = [[]]
    while (this.index < this.source.length) {
      const character = this.source[this.index]
      if (character === ')' && insideGroup) {
        this.index += 1
        return branches
      }
      if (character === '|') {
        this.index += 1
        branches.push([])
        continue
      }
      const node = this.parseAtom()
      const quantifier = this.parseQuantifier()
      if (quantifier !== undefined) {
        node.quantifier = quantifier
      }
      branches[branches.length - 1].push(node)
    }
    return branches
  }

  private parseAtom(): RegexNode {
    const character = this.source[this.index]
    switch (character) {
      case '\\':
        return this.parseEscape()
      case '[':
        return this.parseCharacterClass()
      case '(':
        return this.parseGroup()
      case '.':
        this.index += 1
        return { kind: 'set', source: '.' }
      case '^':
      case '$':
        this.index += 1
        return { kind: 'assertion' }
      default:
        this.index += 1
        return { kind: 'char', char: character.toLowerCase() }
    }
  }

  private parseEscape(): RegexNode {
    const start = this.index
    this.index += 1 // the backslash
    const escaped = this.source[this.index] ?? ''
    this.index += 1

    if (/[1-9]/.test(escaped)) {
      while (/[0-9]/.test(this.source[this.index] ?? '')) this.index += 1
      return { kind: 'backreference' }
    }
    if (escaped === 'k' && this.source[this.index] === '<') {
      const close = this.source.indexOf('>', this.index)
      this.index = close === -1 ? this.source.length : close + 1
      return { kind: 'backreference' }
    }
    if (escaped === 'b' || escaped === 'B') {
      return { kind: 'assertion' }
    }
    if ('dDwWsS'.includes(escaped)) {
      return { kind: 'set', source: `\\${escaped}` }
    }
    if ((escaped === 'p' || escaped === 'P') && this.source[this.index] === '{') {
      const close = this.source.indexOf('}', this.index)
      this.index = close === -1 ? this.source.length : close + 1
      return { kind: 'set', source: this.source.slice(start, this.index) }
    }
    if (escaped === 'x') {
      this.index += 2
      return { kind: 'set', source: this.source.slice(start, this.index) }
    }
    if (escaped === 'u') {
      if (this.source[this.index] === '{') {
        const close = this.source.indexOf('}', this.index)
        this.index = close === -1 ? this.source.length : close + 1
      } else {
        this.index += 4
      }
      return { kind: 'set', source: this.source.slice(start, this.index) }
    }
    if (escaped === 'c') {
      this.index += 1
      return { kind: 'set', source: this.source.slice(start, this.index) }
    }
    const control = CONTROL_ESCAPE_CHARS[escaped]
    return { kind: 'char', char: control ?? escaped.toLowerCase() }
  }

  private parseCharacterClass(): RegexNode {
    const start = this.index
    this.index += 1 // `[`
    if (this.source[this.index] === '^') this.index += 1
    while (this.index < this.source.length) {
      const character = this.source[this.index]
      if (character === '\\') {
        this.index += 2
        continue
      }
      this.index += 1
      if (character === ']') break
    }
    return { kind: 'set', source: this.source.slice(start, this.index) }
  }

  private parseGroup(): RegexNode {
    this.index += 1 // `(`
    if (this.source[this.index] === '?') {
      const rest = this.source.slice(this.index)
      const prefix =
        /^\?(?::|=|!|<=|<!)/.exec(rest) ?? /^\?<[^>]*>/.exec(rest) ?? /^\?[a-zA-Z-]*:/.exec(rest)
      this.index += prefix === null ? 1 : prefix[0].length
    }
    return { kind: 'group', branches: this.parseBranches(true) }
  }

  private parseQuantifier(): Quantifier | undefined {
    const character = this.source[this.index]
    let quantifier: Quantifier | undefined
    if (character === '*') {
      quantifier = { min: 0, max: Infinity }
      this.index += 1
    } else if (character === '+') {
      quantifier = { min: 1, max: Infinity }
      this.index += 1
    } else if (character === '?') {
      quantifier = { min: 0, max: 1 }
      this.index += 1
    } else if (character === '{') {
      const braces = /^\{(\d+)(,(\d*))?\}/.exec(this.source.slice(this.index))
      if (braces !== null) {
        const min = Number(braces[1])
        const max = braces[2] === undefined ? min : braces[3] === '' ? Infinity : Number(braces[3])
        quantifier = { min, max }
        this.index += braces[0].length
      }
    }
    if (quantifier !== undefined && this.source[this.index] === '?') {
      this.index += 1 // lazy suffix: same set of paths, same risk
    }
    return quantifier
  }
}

/** The quantifier lets the node match more than once. */
function repeats(quantifier: Quantifier | undefined): boolean {
  return quantifier !== undefined && quantifier.max > 1
}

/** The quantifier admits more than one repetition count (`?`, `+`, `{1,3}` — not `{2}`). */
function isVariable(quantifier: Quantifier | undefined): boolean {
  return quantifier !== undefined && quantifier.max > quantifier.min
}

function isFixedMandatoryChar(node: RegexNode): node is Extract<RegexNode, { kind: 'char' }> {
  return (
    node.kind === 'char' &&
    (node.quantifier === undefined ||
      (node.quantifier.min === node.quantifier.max && node.quantifier.min >= 1))
  )
}

/** True when some character `set` might match `char` (case-insensitively, to stay conservative). */
function setMayMatchChar(setSource: string, char: string): boolean {
  try {
    return new RegExp(`^(?:${setSource})$`, 'i').test(char)
  } catch {
    return true
  }
}

/** True when `node`, at any repetition, might consume the character `char`. */
function nodeMayConsume(node: RegexNode, char: string): boolean {
  switch (node.kind) {
    case 'assertion':
      return false
    case 'char':
      return node.char === char
    case 'set':
      return setMayMatchChar(node.source, char)
    default:
      // A group or a backreference: we do not reason about its content.
      return true
  }
}

function containsVariableRepetition(nodes: readonly RegexNode[]): boolean {
  return nodes.some(
    (node) =>
      isVariable(node.quantifier) ||
      (node.kind === 'group' && node.branches.some(containsVariableRepetition)),
  )
}

/**
 * One iteration of a repeated group is delimited when a fixed literal opens or
 * closes it and none of the variable parts can consume that literal: the input
 * then splits into iterations in exactly one way.
 */
function branchIsDelimited(branch: readonly RegexNode[]): boolean {
  const significant = branch.filter((node) => node.kind !== 'assertion')
  const variableParts = significant.filter(
    (node) => isVariable(node.quantifier) || node.kind === 'group' || node.kind === 'backreference',
  )
  const candidates = [significant[0], significant[significant.length - 1]]
  return candidates.some(
    (delimiter) =>
      delimiter !== undefined &&
      isFixedMandatoryChar(delimiter) &&
      !variableParts.some((part) => part !== delimiter && nodeMayConsume(part, delimiter.char)),
  )
}

type FirstAtom = { kind: 'char'; char: string } | { kind: 'set'; source: string } | 'unknown'

/** What the branch can start with; `'unknown'` when it may be empty or starts with a group. */
function firstAtomOf(branch: readonly RegexNode[]): FirstAtom {
  const first = branch.find((node) => node.kind !== 'assertion')
  if (first === undefined) return 'unknown'
  if (first.quantifier?.min === 0) return 'unknown'
  if (first.kind === 'char') return { kind: 'char', char: first.char }
  if (first.kind === 'set') return { kind: 'set', source: first.source }
  return 'unknown'
}

function alternativesMayOverlap(branches: readonly RegexNode[][]): boolean {
  const firsts = branches.map(firstAtomOf)
  for (let left = 0; left < firsts.length; left += 1) {
    for (let right = left + 1; right < firsts.length; right += 1) {
      const a = firsts[left]
      const b = firsts[right]
      if (a === 'unknown' || b === 'unknown') return true
      if (a.kind === 'char' && b.kind === 'char' && a.char === b.char) return true
      if (a.kind === 'set' && b.kind === 'set') return true
      if (a.kind === 'set' && b.kind === 'char' && setMayMatchChar(a.source, b.char)) return true
      if (a.kind === 'char' && b.kind === 'set' && setMayMatchChar(b.source, a.char)) return true
    }
  }
  return false
}

function findDanger(
  branches: readonly RegexNode[][],
): Exclude<RegexPatternRejectionReason, 'too_long' | 'invalid'> | null {
  for (const branch of branches) {
    for (const node of branch) {
      if (node.kind === 'backreference' && repeats(node.quantifier)) {
        return 'quantified_backreference'
      }
      if (node.kind !== 'group') continue
      if (repeats(node.quantifier)) {
        const nested = node.branches.some(
          (inner) => containsVariableRepetition(inner) && !branchIsDelimited(inner),
        )
        if (nested) return 'nested_quantifier'
        if (node.branches.length > 1 && alternativesMayOverlap(node.branches)) {
          return 'overlapping_alternation'
        }
      }
      const inner = findDanger(node.branches)
      if (inner !== null) return inner
    }
  }
  return null
}

const DANGER_DESCRIPTIONS: Record<
  Exclude<RegexPatternRejectionReason, 'too_long' | 'invalid'>,
  string
> = {
  nested_quantifier: 'repeats a group that itself contains a quantifier',
  overlapping_alternation: 'repeats a group whose alternatives can match the same text',
  quantified_backreference: 'repeats a backreference',
}

/**
 * Decide whether an operator-authored pattern may be compiled and run. Never
 * throws; `matchesRegexPattern` turns a refusal into the error the engines
 * report.
 */
export function screenRegexPattern(pattern: string): RegexPatternScreenResult {
  if (pattern.length > REGEX_PATTERN_MAX_LENGTH) {
    return {
      accepted: false,
      reason: 'too_long',
      message: `Regex pattern rejected: pattern exceeds maximum length of ${REGEX_PATTERN_MAX_LENGTH} characters`,
    }
  }

  try {
    new RegExp(pattern)
  } catch (error) {
    return {
      accepted: false,
      reason: 'invalid',
      message: `Invalid regex pattern "${pattern}": ${error instanceof Error ? error.message : 'Unknown error'}`,
    }
  }

  const danger = findDanger(new RegexTreeParser(pattern).parse())
  if (danger !== null) {
    return {
      accepted: false,
      reason: danger,
      message: `Regex pattern rejected: "${pattern}" ${DANGER_DESCRIPTIONS[danger]}, which can cause catastrophic backtracking (ReDoS)`,
    }
  }
  return { accepted: true }
}
